import moment from "moment";
import { combineEpics, ofType } from "redux-observable";
import { debounceTime, filter, flatMap, map, switchMap } from "rxjs/operators";
import JobProApi from "../../dependencies/JobProApi";
import schedulerStorage from "../../dependencies/Storage";
import { toggleDeleteModal } from "../../redux/modals";
import { openBid } from "../../redux/searchBids";
import {
  openPurchaseOrder,
  openInventoryPurchaseOrder,
} from "../../redux/searchPurchaseOrders";
import matchCrews from "../../utils/matchCrews";
import matchEquipment from "../../utils/matchEquipment";
import { openJob } from "../searchJobs";
import { openDatasheet, openCustomer } from "../searchAppointments";
import { LOAD_SAVED_SETTINGS } from "../settings";
import { isNumber } from "lodash";

const APPOINTMENT_FIELDS = "id,name";
const CUSTOMER_FIELDS = "id,name";
const CREW_FIELDS =
  "id,equipment(id,color,displayOrder,name,workCapacities(capacityType,value)),resources(resource(id,firstName,lastName),resourcePercentage)";
const JOB_FIELDS =
  "id,capacities(estimatedDuration,laborAmount,materialQuantity),contacts(isPrimaryContact,name,firstName,lastName,cellPhone,workPhone,email),customer(id,name),estimatedStartDate,items(item(itemType(id,name)),proposedQuantity),geoPoint(latitude,longitude),jobNumber,jobTotal,jobType,location,office(id,name),name,profit(actualProfit,proposedProfit),salesperson(calendarColor),status,priorityNotes";
const CALENDAR_FIELDS = `id,crew(${CREW_FIELDS}),description,displayOrder,endDate,endTime,job(${JOB_FIELDS}),startDate,startTime,appointmenttype(${APPOINTMENT_FIELDS}),customer(${CUSTOMER_FIELDS}),`;
const PURCHASE_ORDER_FIELDS = `id,crew(${CREW_FIELDS}),dateDelivered,deliveryOrder,items(id,item(itemType(id,inventoryQuantityLabel,name)),job(id,geoPoint(latitude,longitude),jobNumber,name),quantity,summary),loadCapacity,name,poNumber,shipTo,status(name),supplier(name)`;
const INVENTORY_PURCHASE_ORDER_FIELDS = `id,crew(${CREW_FIELDS}),deliveryDate,deliveryOrder,items(id,item(itemType(id,inventoryQuantityLabel,name)),job(id,geoPoint(latitude,longitude),jobNumber,name),quantity,summary),loadCapacity,name,poNumber,shipTo,status,supplier(name)`;
// Action types
export const CALENDAR_FETCH = "CALENDAR_FETCH";
export const CALENDAR_UPDATE = "CALENDAR_UPDATE";
export const CALENDAR_UPDATE_APPT = "CALENDAR_UPDATE_APPT";

const RESCHEDULE_EVENT = "RESCHEDULE_EVENT";
const RESCHEDULE_EVENT_EXPLICIT = "RESCHEDULE_EVENT_EXPLICIT";
const SCHEDULE_BID_EXPLICIT = "SCHEDULE_BID_EXPLICIT";
const SCHEDULE_JOB_EXPLICIT = "SCHEDULE_JOB_EXPLICIT";
const SCHEDULE_PURCHASE_ORDER_EXPLICIT = "SCHEDULE_PURCHASE_ORDER_EXPLICIT";
const SET_ACTIVE_CREW = "SET_ACTIVE_CREW";
const SET_ACTIVE_EQUIPMENT = "SET_ACTIVE_EQUIPMENT";
const TOGGLE_BIDS = "TOGGLE_BIDS";
const TOGGLE_DELIVERIES = "TOGGLE_DELIVERIES";
const TOGGLE_JOBS = "TOGGLE_JOBS";
const TOGGLE_APPOINTMENTS = "TOGGLE_APPOINTMENTS";
const UPDATE_SELECTED_EMPLOYEE_LIST = "UPDATE_SELECTED_EMPLOYEE_LIST";
const SELECTED_WEEK_VIEW = "SELECTED_WEEK_VIEW";
const SCHEDULE_APPOINTMENT_EXPLICIT = "SCHEDULE_APPOINTMENT_EXPLICIT";
const SCHEDULE_INDIVIDUAL_APPOINTMENTS_EXPLICIT =
  "SCHEDULE_INDIVIDUAL_APPOINTMENTS_EXPLICIT";
const SPLIT_APPOINTMENT_EXPLICIT = "SPLIT_APPOINTMENT_EXPLICIT";
const SPLIT_EVENT_EXPLICIT = "SPLIT_EVENT_EXPLICIT";
const APPT_CONTEXT_MENU_CLICK = "APPT_CONTEXT_MENU_CLICK";

const HIDE_CONTEXT_MENU_ITEM = "HIDE_CONTEXT_MENU_ITEM";

function getDefaultState() {
  try {
    return {
      activeCrew: null,
      activeEquipment: null,
      date: moment().format("YYYY-MM-DD"),
      mode: localStorage.getItem("mode") || "week",
      showBids: localStorage.getItem("hideBids") !== "true",
      showDeliveries: localStorage.getItem("hideDeliveries") !== "true",
      showJobs: localStorage.getItem("hideJobs") !== "true",
      showAppointments: localStorage.getItem("hideAppointments") !== "true",
      selected_employees: [],
      selected_week_view: "equipment",
      showApptMenuItem: false,
      showMenuItem: localStorage.getItem("hideMenuItem") !== "true",
    };
  } catch (e) {
    return {
      activeCrew: null,
      activeEquipment: null,
      date: moment().format("YYYY-MM-DD"),
      mode: "week",
      showBids: true,
      showDeliveries: true,
      showJobs: true,
      showAppointments: true,
      selected_employees: [],
      selected_week_view: "equipment",
      showMenuItem: true,
    };
  }
}

const DEFAULT_STATE = getDefaultState();

/**
 * Determines the appropriate end date for a rescheduled calendar event.
 *
 * @param {any} event The event being rescheduled.
 * @param {any} scheduleDate The new date the event is scheduled.
 * @returns The new event end date.
 */
function getEndDate(event, scheduleDate) {
  // If there is no existing endDate, return the schedule date
  if (!event.endDate) {
    return moment(scheduleDate).format("YYYY-MM-DD");
  }

  // Determine the difference in the new schedule date and the start date
  const dateDiff = moment(scheduleDate).diff(event.startDate, "days");

  // Apply the date difference to the current end date
  return moment(event.endDate).add(dateDiff, "days").format("YYYY-MM-DD");
}

export function getNextPurchaseOrderDeliveryOrder(events, crew) {
  const purchaseOrderEvents = events
    .filter((e) => !!e.purchaseOrder || !!e.inventoryPurchaseOrder)
    .filter((e) => matchCrews(crew, e.crew));

  if (purchaseOrderEvents.length === 0) {
    return 1;
  }

  // Purchase order deliveries, normal pos or inventory pos
  const maxDeliveryOrder = Math.max(
    ...purchaseOrderEvents.map((e) => {
      if (e.purchaseOrder) {
        return e.purchaseOrder.deliveryOrder || 1;
      }
      if (e.inventoryPurchaseOrder) {
        return e.inventoryPurchaseOrder.deliveryOrder || 1;
      }
      return 1;
    })
  );

  return maxDeliveryOrder + 1;
}

export function getNextCalendarDisplayOrder(events, crew) {
  return (
    events
      .filter((e) => !e.purchaseOrder)
      .filter((e) => matchCrews(crew, e.crew))
      .reduce((prev, e) => Math.max(prev, e.displayOrder), 0) + 1
  );
}

export function getNextCalendarDisplayOrderAppointment(events) {
  return (
    events
      .filter((e) => !e.purchaseOrder)
      .reduce((prev, e) => Math.max(prev, e.displayOrder), 0) + 1
  );
}

export function getNextCalendarDisplayOrderEquipment(events, equipment) {
  return (
    events
      .filter((e) => !e.purchaseOrder)
      .filter((e) =>
        matchEquipment(
          equipment,
          e.crew && e.crew.equipment ? e.crew.equipment : null
        )
      )
      .reduce((prev, e) => Math.max(prev, e.displayOrder), 0) + 1
  );
}

// Reducer
export default function (state = DEFAULT_STATE, { payload, type }) {
  switch (type) {
    case CALENDAR_FETCH:
      try {
        localStorage.setItem("mode", payload.mode);
      } catch (e) {}
      return {
        ...state,
        ...payload,
        date: moment(payload.date).format("YYYY-MM-DD"),
      };
    case SET_ACTIVE_CREW:
      return {
        ...state,
        activeCrew: payload,
      };
    case SET_ACTIVE_EQUIPMENT:
      return {
        ...state,
        activeEquipment: payload,
      };
    case TOGGLE_BIDS:
      try {
        localStorage.setItem("hideBids", state.showBids);
      } catch (e) {}
      return {
        ...state,
        showBids: !state.showBids,
      };
    case TOGGLE_DELIVERIES:
      try {
        localStorage.setItem("hideDeliveries", state.showDeliveries);
      } catch (e) {}
      return {
        ...state,
        showDeliveries: !state.showDeliveries,
      };
    case TOGGLE_JOBS:
      try {
        localStorage.setItem("hideJobs", state.showJobs);
      } catch (e) {}
      return {
        ...state,
        showJobs: !state.showJobs,
      };

    case TOGGLE_APPOINTMENTS:
      try {
        localStorage.setItem("hideAppointments", state.showAppointments);
      } catch (e) {}
      return {
        ...state,
        showAppointments: !state.showAppointments,
      };

    case UPDATE_SELECTED_EMPLOYEE_LIST:
      let selected_Employees = [...state.selected_employees];
      try {
        let empid = parseInt(payload.payload.empid);
        if (payload.payload.selected) {
          if (selected_Employees.indexOf(empid) === -1) {
            // add if does not exist
            selected_Employees = [...state.selected_employees, empid];
          }
        } else {
          if (selected_Employees.indexOf(empid) !== -1) {
            // remove  if exist and employee not selected
            selected_Employees.splice(selected_Employees.indexOf(empid), 1);
          }
        }
        state.selected_employees = selected_Employees;
      } catch (e) {
        console.log(e);
      }
      return {
        ...state,
        selected_Employees,
      };
    case SELECTED_WEEK_VIEW:
      return {
        ...state,
        selected_week_view: payload,
      };
    case APPT_CONTEXT_MENU_CLICK:
      return {
        ...state,
        showApptMenuItem: payload,
      };

    case HIDE_CONTEXT_MENU_ITEM:
      try {
        localStorage.setItem("hideMenuItem", state.showMenuItem);
      } catch (e) {}
      return {
        ...state,
        showMenuItem: payload,
      };

    default:
      return state;
  }
}

const mapBidToEvent = (b) => ({
  id: `BID-${b.id}`,
  key: Math.random(),
  crew: null,
  startDate: b.estimatedStartDate,
  endDate: b.estimatedStartDate,
  bid: b,
  tentative: true,
});

const mapCalendarEventToEvent = (c) => ({
  ...c,
  key: Math.random(),
  evttype: c.job != null ? "jobs" : "appointments",
});

const mapPurchaseOrderToEvent = (p) => ({
  id: Math.random(),
  crew: p.crew,
  startDate: p.dateDelivered,
  endDate: p.dateDelivered,
  purchaseOrder: p,
  evttype: "purchaseOrders",
  mode: localStorage.getItem("mode") || "week",
});

const mapInventoryPurchaseOrderToEvent = (p) => ({
  id: Math.random(),
  crew: p.crew,
  startDate: p.deliveryDate,
  endDate: p.deliveryDate,
  inventoryPurchaseOrder: {
    ...p,
    iD: p.id || p.iD, // Ensure both id and iD are available
  },
  evttype: "purchaseOrders",
  mode: localStorage.getItem("mode") || "week",
});

const performScheduleJob = (jobId, crew, date, displayOrder, mode) => {
  //if(!mode)
  //mode=localStorage.getItem('mode') || 'week';
  return JobProApi.calendar
    .create(
      {
        crew: crew
          ? {
              equipment: crew.equipment,
              resources: crew.resources
                ? crew.resources
                    .filter((r) => !!r.resource)
                    .map((r) => ({
                      resource: { id: r.resource.id },
                      resourcePercentage: r.resourcePercentage,
                    }))
                : [],
            }
          : null,
        displayOrder,
        endDate: moment(date).format("YYYY-MM-DD"),
        job: jobId ? { id: jobId } : null,
        startDate: moment(date).format("YYYY-MM-DD"),
      },
      {
        fields: CALENDAR_FIELDS,
      }
    )
    .then((event) => {
      event.evttype = "jobs";
      event.mode = localStorage.getItem("mode") || "week";
      return {
        type: CALENDAR_UPDATE,
        payload: event,
      };
    });
};

const performScheduleIndividualAppointmentsEvent = (
  datasheetid,
  customerid,
  appointmenttype,
  crew,
  startdate,
  starttime,
  enddate,
  endtime,
  description,
  jobid
) => {
  return JobProApi.calendar
    .createSplitEvents(
      {
        Crew:
          crew && crew.crew
            ? {
                Equipment: crew.crew.equipment,
                Resources: crew.crew.resources
                  ? crew.crew.resources
                      .filter((r) => !!r.resource)
                      .map((r) => ({
                        resource: { id: r.resource.id },
                      }))
                  : [],
              }
            : null,
        Equipment: crew ? crew.equipment : null,
        EndDate: moment(enddate).format("YYYY-MM-DD"),
        customer: customerid ? { id: customerid } : null,
        datasheet: datasheetid ? { id: datasheetid } : null,
        job: jobid ? { id: jobid } : null,
        ApptType: appointmenttype,
        description: description,
        StartDate: moment(startdate).format("YYYY-MM-DD"),
        StartTime: starttime,
        EndTime: endtime,
      },
      {
        fields: CALENDAR_FIELDS,
      }
    )
    .then(() => ({
      type: CALENDAR_FETCH,
      payload: { mode: "week", date: startdate },
    }));
};

const performSplitAppointmentEvent = (id, startdate) => {
  return JobProApi.calendar
    .split(id, {
      fields:
        "startDate,endDate,startTime,endTime,description,crew(id),job(id),equipment(id),description,apptType,datasheet(id),associatedId,customer(id)",
    })
    .then(() => ({
      type: CALENDAR_FETCH,
      payload: { mode: "week", date: startdate },
    }));
};

const performSplitEvent = (id, startdate) => {
  return JobProApi.calendar
    .split(id, {
      fields:
        "startDate,endDate,startTime,endTime,description,crew(id),job(id,priorityNotes),equipment(id),description,apptType,datasheet(id),associatedId,customer(id)",
    })
    .then(() => ({
      type: CALENDAR_FETCH,
      payload: { mode: "week", date: startdate },
    }));
};

const performSchedulePurchaseOrder = (
  purchaseOrderId,
  crew,
  date,
  deliveryOrder
) => {
  return JobProApi.purchaseOrders
    .update(
      purchaseOrderId,
      {
        crew: crew
          ? {
              equipment: crew.equipment ? { id: crew.equipment.id } : null,
              resources:
                crew.resources &&
                crew.resources
                  .filter((r) => !!r.resource)
                  .map((r) => ({
                    resource: { id: r.resource.id },
                    resourcePercentage: r.resourcePercentage,
                  })),
            }
          : null,
        dateDelivered: moment(date).format("YYYY-MM-DD"),
        deliveryOrder,
      },
      {
        fields: PURCHASE_ORDER_FIELDS,
      }
    )
    .then((purchaseOrder) => ({
      type: CALENDAR_UPDATE,
      payload: mapPurchaseOrderToEvent(purchaseOrder),
    }));
};

const performScheduleInventoryPurchaseOrder = (
  purchaseOrderId,
  crew,
  date,
  deliveryOrder
) => {
  return JobProApi.inventoryPurchaseOrders
    .update(
      purchaseOrderId,
      {
        crew: crew
          ? {
              equipment: crew.equipment ? { id: crew.equipment.id } : null,
              resources:
                crew.resources &&
                crew.resources
                  .filter((r) => !!r.resource)
                  .map((r) => ({
                    resource: { id: r.resource.id },
                    resourcePercentage: r.resourcePercentage,
                  })),
            }
          : null,
        deliveryDate: moment(date).format("YYYY-MM-DD"),
        deliveryOrder,
      },
      {
        fields: INVENTORY_PURCHASE_ORDER_FIELDS,
      }
    )
    .then((purchaseOrder) => ({
      type: CALENDAR_UPDATE,
      payload: mapInventoryPurchaseOrderToEvent(purchaseOrder),
    }));
};

const performScheduleAppointmentEvent = (
  datasheetid,
  customerid,
  appointmenttype,
  crew,
  startdate,
  starttime,
  enddate,
  endtime,
  displayOrder,
  description,
  jobid,
  mode
) => {
  //if(!mode)
  //mode=localStorage.getItem('mode') || 'week';

  return JobProApi.calendar
    .create(
      {
        Crew:
          crew && crew.crew
            ? {
                Equipment: crew.crew.equipment,
                Resources: crew.crew.resources
                  ? crew.crew.resources
                      .filter((r) => !!r.resource)
                      .map((r) => ({
                        resource: { id: r.resource.id },
                      }))
                  : [],
              }
            : null,
        Equipment: crew ? crew.equipment : null,
        displayOrder,
        EndDate: moment(enddate).format("YYYY-MM-DD"),
        customer: customerid ? { id: customerid } : null,
        datasheet: datasheetid ? { id: datasheetid } : null,
        job: jobid ? { id: jobid } : null,
        ApptType: appointmenttype,
        description: description,
        StartDate: moment(startdate).format("YYYY-MM-DD"),
        StartTime: starttime,
        EndTime: endtime,
      },
      {
        fields: CALENDAR_FIELDS,
      }
    )
    .then(() => ({
      type: CALENDAR_FETCH,
      payload: {
        mode: localStorage.getItem("mode") || "week",
        date: startdate,
      },
    }));
};

export const fetchCalendar = (date, mode) => ({
  type: CALENDAR_FETCH,
  payload: {
    date,
    mode,
  },
});

export const reloadCalendar = () => (dispatch, getState) => {
  const { calendar } = getState();
  const { date, mode } = calendar;
  dispatch(fetchCalendar(date, mode));
};

export const scheduleBid = (bidId, date) => (dispatch, getState) => {
  const { calendar } = getState();
  date = date || calendar.date;

  return JobProApi.bids
    .update(
      bidId,
      {
        startDate: moment(date).format("YYYY-MM-DD"),
      },
      { fields: "id" }
    )
    .then(() => {
      return dispatch(reloadCalendar());
    });
};

export const scheduleJobExplicit = (jobId, crew, date, mode) => ({
  type: SCHEDULE_JOB_EXPLICIT,
  payload: { jobId, crew, date, mode },
});

export const scheduleJobToActiveCrew = (jobId) => (dispatch, getState) => {
  const { calendar, events } = getState();
  const { activeCrew, date } = calendar;

  const nextDisplayOrder = getNextCalendarDisplayOrder(
    events.events,
    activeCrew
  );
  return performScheduleJob(jobId, activeCrew, date, nextDisplayOrder).then(
    dispatch
  );
};

export const rescheduleEvent = (eventId) => ({
  type: RESCHEDULE_EVENT,
  payload: { eventId },
});

export const rescheduleEventExplicit = (eventId, date, crew, mode) => ({
  type: RESCHEDULE_EVENT_EXPLICIT,
  payload: { eventId, date, crew, mode },
});

export const scheduleAppointmentExplicit = (
  datasheetid,
  customerid,
  appointmentype,
  crew,
  startdate,
  starttime,
  enddate,
  endtime,
  description,
  jobid,
  mode
) => ({
  type: SCHEDULE_APPOINTMENT_EXPLICIT,
  payload: {
    datasheetid,
    customerid,
    appointmentype,
    crew,
    startdate,
    starttime,
    enddate,
    endtime,
    description,
    jobid,
    mode,
  },
});

export const scheduleIndividualAppointmentsExplicit = (
  datasheetid,
  customerid,
  appointmentype,
  crew,
  startdate,
  starttime,
  enddate,
  endtime,
  description,
  jobid
) => ({
  type: SCHEDULE_INDIVIDUAL_APPOINTMENTS_EXPLICIT,
  payload: {
    datasheetid,
    customerid,
    appointmentype,
    crew,
    startdate,
    starttime,
    enddate,
    endtime,
    description,
    jobid,
  },
});

export const splitAppointmentExplicit = (id, startdate) => ({
  type: SPLIT_APPOINTMENT_EXPLICIT,
  payload: {
    id,
    startdate,
  },
});

export const splitEventExplicit = (id, startdate) => ({
  type: SPLIT_EVENT_EXPLICIT,
  payload: {
    id,
    startdate,
  },
});

export const schedulePurchaseOrderExplicit = (
  purchaseOrderId,
  date,
  crew,
  isInventoryPO
) => ({
  type: SCHEDULE_PURCHASE_ORDER_EXPLICIT,
  payload: { purchaseOrderId, date, crew, isInventoryPO },
});

export const schedulePurchaseOrderToActiveCrew =
  (purchaseOrderId, isInventoryPO) => (dispatch, getState) => {
    const { calendar, events } = getState();
    const { activeCrew, date } = calendar;

    // Find the largest delivery order and increment it by one
    const nextDeliveryOrder = getNextPurchaseOrderDeliveryOrder(
      events.events,
      activeCrew
    );

    if (isInventoryPO) {
      return performScheduleInventoryPurchaseOrder(
        purchaseOrderId,
        activeCrew,
        date,
        nextDeliveryOrder
      ).then(dispatch);
    } else {
      return performSchedulePurchaseOrder(
        purchaseOrderId,
        activeCrew,
        date,
        nextDeliveryOrder
      ).then(dispatch);
    }
  };

export const setActiveCrew = (crew) => ({
  type: SET_ACTIVE_CREW,
  payload: crew,
});

export const setActiveEquipment = (equipment) => ({
  type: SET_ACTIVE_EQUIPMENT,
  payload: equipment,
});

export const showApptContextMenuItem = (event) => ({
  type: APPT_CONTEXT_MENU_CLICK,
  payload: event.evt.customer != null || event.evt.datasheet != null,
});

export const changeWeekView = (view) => ({
  type: SELECTED_WEEK_VIEW,
  payload: view,
});

export const toggleBids = () => ({
  type: TOGGLE_BIDS,
});

export const toggleDeliveries = () => ({
  type: TOGGLE_DELIVERIES,
});

export const toggleJobs = () => ({
  type: TOGGLE_JOBS,
});

export const toggleAppoinments = () => ({
  type: TOGGLE_APPOINTMENTS,
});

export const updateEvent = (eventId, changes) => (dispatch, getState) => {
  JobProApi.calendar
    .update(eventId, changes, {
      fields: CALENDAR_FIELDS,
    })
    .then((event) => {
      const { date, mode } = getState().calendar;
      dispatch(fetchCalendar(event.startDate, mode));
    })
    .catch((err) => {
      throw err;
    });
};

export const updatePurchaseOrder = (purchaseOrderId, changes) => (dispatch) => {
  JobProApi.purchaseOrders
    .update(purchaseOrderId, changes, {
      fields: PURCHASE_ORDER_FIELDS,
    })
    .then(() => {
      dispatch(reloadCalendar());
    })
    .catch((err) => {
      throw err;
    });
};

export const updateInventoryPurchaseOrder =
  (purchaseOrderId, changes) => (dispatch) => {
    JobProApi.inventoryPurchaseOrders
      .update(purchaseOrderId, changes, {
        fields: INVENTORY_PURCHASE_ORDER_FIELDS,
      })
      .then(() => {
        dispatch(reloadCalendar());
      })
      .catch((err) => {
        throw err;
      });
  };

export const deleteEvent = () => (dispatch, getState) => {
  const { events, modals } = getState();
  const { eventId } = modals.deleteModal;
  const event = events.events.find((e) => e.id === eventId);

  dispatch(toggleDeleteModal());
  console.log("event", event);
  if (event.purchaseOrder || event.inventoryPurchaseOrder) {
    if (event.inventoryPurchaseOrder) {
      dispatch(
        updateInventoryPurchaseOrder(event.inventoryPurchaseOrder.iD, {
          crew: null,
          deliveryDate: null,
        })
      );
    } else {
      dispatch(
        updatePurchaseOrder(event.purchaseOrder.id, {
          crew: null,
          dateDelivered: null,
        })
      );
    }
  } else {
    JobProApi.calendar.delete(eventId).then(() => {
      const { date, mode } = getState().calendar;
      dispatch(fetchCalendar(date, mode));
    });
  }
};

export const openEvent = (eventId) => (dispatch, getState) => {
  const { events } = getState().events;
  const event = events.find((e) => e.id === eventId);
  if (event.bid) {
    openBid(event.bid.id);
  } else if (event.purchaseOrder) {
    openPurchaseOrder(event.purchaseOrder.id);
  } else if (event.inventoryPurchaseOrder) {
    openInventoryPurchaseOrder(event.inventoryPurchaseOrder.iD);
  } else if (event.job) {
    openJob(event.job.id);
  }
};

// passing event directly
export const openApptEvent = (event) => () => {
  if (event.datasheet) {
    openDatasheet(event.datasheet.id);
  } else if (event.customer) {
    openCustomer(event.customer.id);
  }
};

export const hideMenuItem = (payload) => ({
  type: HIDE_CONTEXT_MENU_ITEM,
  payload: isNumber(payload),
});
export const updateSelectedEmployeeList = (payload) => ({
  type: UPDATE_SELECTED_EMPLOYEE_LIST,
  payload: { payload },
});

const loadSavedCalendarComplete = (savedSettings) => (dispatch, getState) => {
  const { calendar } = getState();
  const { date } = calendar;
  const { mode } = savedSettings;
  dispatch(fetchCalendar(date, mode));
};

const performReschedule = (event, crew, date, events, settings, mode) => {
  const eventId = event.id;
  if (!mode) localStorage.getItem("mode") || "week";

  if (event.inventoryPurchaseOrder) {
    const nextDeliveryOrder = getNextPurchaseOrderDeliveryOrder(events, crew);
    const inventoryPurchaseOrderId =
      event.inventoryPurchaseOrder.iD || event.inventoryPurchaseOrder.id;

    return JobProApi.inventoryPurchaseOrders
      .update(
        inventoryPurchaseOrderId,
        {
          crew: crew
            ? {
                equipment: crew.equipment ? { id: crew.equipment.id } : null,
                resources: crew.resources
                  .filter((r) => !!r.resource)
                  .map((r) => ({
                    resource: { id: r.resource.id },
                    resourcePercentage: r.resourcePercentage,
                  })),
              }
            : null,
          deliveryDate: moment(date).format("YYYY-MM-DD"),
          deliveryOrder: nextDeliveryOrder,
        },
        {
          fields: INVENTORY_PURCHASE_ORDER_FIELDS,
        }
      )
      .then((inventoryPurchaseOrder) => ({
        type: CALENDAR_UPDATE,
        payload: {
          ...mapInventoryPurchaseOrderToEvent(inventoryPurchaseOrder),
          id: eventId,
          mode: mode,
        },
      }));
  } else if (event.bid) {
    return JobProApi.bids
      .update(
        event.bid.id,
        {
          startDate: moment(date).format("YYYY-MM-DD"),
        },
        { fields: "id" }
      )
      .then(() => {
        return reloadCalendar();
      });
  } else if (event.purchaseOrder) {
    const nextDeliveryOrder = getNextPurchaseOrderDeliveryOrder(events, crew);

    return JobProApi.purchaseOrders
      .update(
        event.purchaseOrder.id,
        {
          crew: crew
            ? {
                equipment: crew.equipment ? { id: crew.equipment.id } : null,
                resources: crew.resources
                  .filter((r) => !!r.resource)
                  .map((r) => ({
                    resource: { id: r.resource.id },
                    resourcePercentage: r.resourcePercentage,
                  })),
              }
            : null,
          dateDelivered: moment(date).format("YYYY-MM-DD"),
          deliveryOrder: nextDeliveryOrder,
        },
        {
          fields: PURCHASE_ORDER_FIELDS,
        }
      )
      .then((purchaseOrder) => ({
        type: CALENDAR_UPDATE,
        payload: {
          ...mapPurchaseOrderToEvent(purchaseOrder),
          id: eventId,
          mode: mode,
        },
      }));
  } else if (event.job && event.tentative) {
    return scheduleJobExplicit(
      event.job.id,
      crew,
      date,
      localStorage.getItem("mode") || "week"
    );
  } else {
    const nextDisplayOrder =
      settings.schedulerView == "crew"
        ? getNextCalendarDisplayOrder(events, crew)
        : getNextCalendarDisplayOrderEquipment(
            events,
            crew && crew.equipment ? crew.equipment : null
          );

    return JobProApi.calendar
      .update(
        eventId,
        {
          crew: crew
            ? {
                equipment: crew.equipment ? { id: crew.equipment.id } : null,
                resources: crew.resources
                  ? crew.resources
                      .filter((r) => !!r.resource)
                      .map((r) => ({
                        resource: { id: r.resource.id },
                        resourcePercentage: r.resourcePercentage,
                      }))
                  : [],
              }
            : null,
          displayOrder: nextDisplayOrder,
          startDate: moment(date).format("YYYY-MM-DD"),
          endDate: getEndDate(event, date),
          customer: event.customer ? { id: event.customer.id } : null,
          datasheet: event.datasheet
            ? { id: event.datasheet.datasheetid }
            : null,
          ApptType: event.appttype ? event.apptype : null,
          description: event.description,
          StartTime: event.startTime,
          EndTime: event.endTime,
        },
        {
          fields: CALENDAR_FIELDS,
        }
      )
      .then((event) => ({
        type: CALENDAR_FETCH,
        payload: { mode: mode, date: date },
      }));
  }
};

// Epics
const loadSavedCalendarEpic = (action$) =>
  action$.pipe(
    ofType(LOAD_SAVED_SETTINGS),
    switchMap(() => schedulerStorage.getItem("calendar")),
    map((calendar) => loadSavedCalendarComplete(calendar || {}))
  );

const rescheduleEventEpic = (action$, state$) =>
  action$.pipe(
    ofType(RESCHEDULE_EVENT),
    map(({ payload }) => payload),
    flatMap(({ eventId }) => {
      const { calendar, events, settings } = state$.value;
      const { activeCrew, date } = calendar;

      const event = events.events.find((e) => e.id === eventId);

      return performReschedule(
        event,
        activeCrew,
        date,
        events.events,
        settings
      );
    })
  );

const rescheduleEventExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(RESCHEDULE_EVENT_EXPLICIT),
    map(({ payload }) => payload),
    flatMap(({ crew, date, eventId, mode }) => {
      const { events, settings } = state$.value;

      const event = events.events.find((e) => e.id === eventId);

      return performReschedule(
        event,
        crew,
        date,
        events.events,
        settings,
        mode
      );
    })
  );

const saveCalendarEpic = (action$, state$) =>
  action$.pipe(
    ofType(CALENDAR_FETCH),
    debounceTime(1000),
    map(() => {
      const { mode } = state$.value.calendar;
      return {
        mode,
      };
    }),
    switchMap((calendar) => schedulerStorage.setItem("calendar", calendar)),
    filter(() => false)
  );

const scheduleBidExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(SCHEDULE_BID_EXPLICIT),
    map(({ payload }) => payload),
    flatMap(({ crew, date, jobId }) => {
      const nextDisplayOrder = getNextCalendarDisplayOrder(
        state$.value.events.events,
        crew
      );
      return performScheduleJob(jobId, crew, date, nextDisplayOrder);
    })
  );

const scheduleJobExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(SCHEDULE_JOB_EXPLICIT),
    map(({ payload }) => payload),
    flatMap(({ crew, date, jobId }) => {
      const nextDisplayOrder = getNextCalendarDisplayOrder(
        state$.value.events.events,
        crew
      );
      return performScheduleJob(jobId, crew, date, nextDisplayOrder);
    })
  );

const scheduleAppointmentExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(SCHEDULE_APPOINTMENT_EXPLICIT),
    map(({ payload }) => {
      return payload;
    }),
    flatMap(
      ({
        datasheetid,
        customerid,
        appointmentype,
        crew,
        startdate,
        starttime,
        enddate,
        endtime,
        description,
        jobid,
      }) => {
        const nextDisplayOrder = getNextCalendarDisplayOrderAppointment(
          state$.value.events.events,
          crew
        );
        return performScheduleAppointmentEvent(
          datasheetid,
          customerid,
          appointmentype,
          crew,
          startdate,
          starttime,
          enddate,
          endtime,
          nextDisplayOrder,
          description,
          jobid
        );
      }
    )
  );

const scheduleIndividualAppointmentsExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(SCHEDULE_INDIVIDUAL_APPOINTMENTS_EXPLICIT),
    map(({ payload }) => {
      return payload;
    }),
    flatMap(
      ({
        datasheetid,
        customerid,
        appointmentype,
        crew,
        startdate,
        starttime,
        enddate,
        endtime,
        description,
        jobid,
      }) => {
        return performScheduleIndividualAppointmentsEvent(
          datasheetid,
          customerid,
          appointmentype,
          crew,
          startdate,
          starttime,
          enddate,
          endtime,
          description,
          jobid
        );
      }
    )
  );

const splitAppointmentExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(SPLIT_APPOINTMENT_EXPLICIT),
    map(({ payload }) => {
      return payload;
    }),
    flatMap(({ id, startdate }) => {
      return performSplitAppointmentEvent(id, startdate);
    })
  );

const splitEventExplicitEpic = (action$) =>
  action$.pipe(
    ofType(SPLIT_EVENT_EXPLICIT),
    map(({ payload }) => {
      return payload;
    }),
    flatMap(({ id, startdate }) => {
      return performSplitEvent(id, startdate);
    })
  );

const schedulePurchaseOrderExplicitEpic = (action$, state$) =>
  action$.pipe(
    ofType(SCHEDULE_PURCHASE_ORDER_EXPLICIT),
    map(({ payload }) => payload),
    flatMap(({ crew, date, purchaseOrderId, isInventoryPO }) => {
      const nextDeliveryOrder = getNextPurchaseOrderDeliveryOrder(
        state$.value.events.events,
        crew
      );
      if (isInventoryPO) {
        return performScheduleInventoryPurchaseOrder(
          purchaseOrderId,
          crew,
          date,
          nextDeliveryOrder
        );
      } else {
        return performSchedulePurchaseOrder(
          purchaseOrderId,
          crew,
          date,
          nextDeliveryOrder
        );
      }
    })
  );

export const epic = combineEpics(
  loadSavedCalendarEpic,
  rescheduleEventEpic,
  rescheduleEventExplicitEpic,
  saveCalendarEpic,
  scheduleJobExplicitEpic,
  schedulePurchaseOrderExplicitEpic,
  scheduleAppointmentExplicitEpic,
  scheduleIndividualAppointmentsExplicitEpic,
  splitAppointmentExplicitEpic,
  splitEventExplicitEpic
);
